import { useState, useEffect } from 'react';

const BudgetManager = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [currency, setCurrency] = useState('GBP');
  const [budgetData, setBudgetData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [income, setIncome] = useState([]);
  const [monthlyBudgets, setMonthlyBudgets] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check if device is mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Additional styles specific to clear data button
  const clearDataButtonStyle = {
    padding: '6px 12px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    marginLeft: isMobile ? '0' : '8px',
    marginTop: isMobile ? '8px' : '0'
  };

  // Clear all saved data function
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all saved budget data? This action cannot be undone.')) {
      try {
        sessionStorage.removeItem('budgetData');
        sessionStorage.removeItem('budgetExpenses');
        sessionStorage.removeItem('budgetIncome');
        sessionStorage.removeItem('budgetMonthlyBudgets');
        sessionStorage.removeItem('budgetCurrency');
        
        // Reset all state to defaults
        const defaultBudgetData = {
          savingsGoal: { target: 10000, current: 0 },
          monthlyTarget: 350,
          timeToGoal: 24
        };
        
        setBudgetData(defaultBudgetData);
        setExpenses([]);
        setIncome([]);
        setMonthlyBudgets([]);
        setCurrency('GBP');
        setActiveTab('overview');
        
        alert('All budget data cleared successfully!');
      } catch (error) {
        console.error('Error clearing data:', error);
        alert('Error clearing data. Please try again.');
      }
    }
  };

  // ✅ Load data from memory on mount
  useEffect(() => {
    const savedBudgetData = sessionStorage.getItem('budgetData');
    const savedExpenses = sessionStorage.getItem('budgetExpenses');
    const savedIncome = sessionStorage.getItem('budgetIncome');
    const savedMonthlyBudgets = sessionStorage.getItem('budgetMonthlyBudgets');
    const savedCurrency = sessionStorage.getItem('budgetCurrency');

    // Set default budgetData first, then override with saved data if it exists
    const defaultBudgetData = {
      savingsGoal: { target: 10000, current: 0 },
      monthlyTarget: 350,
      timeToGoal: 24
    };

    if (savedBudgetData) {
      try {
        setBudgetData(JSON.parse(savedBudgetData));
      } catch (error) {
        console.error('Error loading budget data:', error);
        setBudgetData(defaultBudgetData);
      }
    } else {
      setBudgetData(defaultBudgetData);
    }

    if (savedExpenses) {
      try {
        setExpenses(JSON.parse(savedExpenses));
      } catch (error) {
        console.error('Error loading expenses:', error);
      }
    }

    if (savedIncome) {
      try {
        setIncome(JSON.parse(savedIncome));
      } catch (error) {
        console.error('Error loading income:', error);
      }
    }

    if (savedMonthlyBudgets) {
      try {
        setMonthlyBudgets(JSON.parse(savedMonthlyBudgets));
      } catch (error) {
        console.error('Error loading monthly budgets:', error);
      }
    }

    if (savedCurrency) {
      try {
        setCurrency(savedCurrency);
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    }
  }, []);

  // ✅ Save budgetData whenever it changes
  useEffect(() => {
    if (budgetData) {
      try {
        sessionStorage.setItem('budgetData', JSON.stringify(budgetData));
      } catch (error) {
        console.error('Error saving budget data:', error);
      }
    }
  }, [budgetData]);

  // ✅ Save expenses whenever they change
  useEffect(() => {
    try {
      if (expenses.length > 0) {
        sessionStorage.setItem('budgetExpenses', JSON.stringify(expenses));
      } else {
        sessionStorage.removeItem('budgetExpenses');
      }
    } catch (error) {
      console.error('Error saving expenses:', error);
    }
  }, [expenses]);

  // ✅ Save income whenever it changes
  useEffect(() => {
    try {
      if (income.length > 0) {
        sessionStorage.setItem('budgetIncome', JSON.stringify(income));
      } else {
        sessionStorage.removeItem('budgetIncome');
      }
    } catch (error) {
      console.error('Error saving income:', error);
    }
  }, [income]);

  // ✅ Save monthlyBudgets whenever they change
  useEffect(() => {
    try {
      if (monthlyBudgets.length > 0) {
        sessionStorage.setItem('budgetMonthlyBudgets', JSON.stringify(monthlyBudgets));
      } else {
        sessionStorage.removeItem('budgetMonthlyBudgets');
      }
    } catch (error) {
      console.error('Error saving monthly budgets:', error);
    }
  }, [monthlyBudgets]);

  // ✅ Save currency whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('budgetCurrency', currency);
    } catch (error) {
      console.error('Error saving currency:', error);
    }
  }, [currency]);

  // Get list of all available currencies
const availableCurrencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
];


  // Base styles object
  const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  todoContainer: {
    maxWidth: '1200px',
    margin: '20px auto',
    padding: isMobile ? '16px' : '24px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    backgroundColor: 'white',          // white card
    borderRadius: '12px',              // rounded corners
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)' // soft shadow
  },
  todoHeader: {
    marginBottom: '24px',
    textAlign: 'center'
  },
  // Responsive title with different sizes for mobile vs laptop
  todoTitle: {
    fontSize: isMobile ? '24px' : '28px', // responsive font size
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 16px 0',
    textAlign: 'left'
  },
  welcomeTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 16px 0'
  },
  addTaskSection: {
    backgroundColor: '#f8fafc',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    marginBottom: '24px'
  },
  currencySelector: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    fontSize: '14px'
  },
  filterButton: {
  padding: isMobile ? '12px 16px' : '16px 32px',   // smaller padding for mobile
  borderRadius: '10px',   // slightly rounder
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  cursor: 'pointer',
  fontSize: isMobile ? '14px' : '16px',       // smaller text for mobile
  fontWeight: '600',
  transition: 'all 0.2s',
  textAlign: 'center',
  flex: isMobile ? 1 : 'none'  // equal width on mobile
},

  filterActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
  },
  filterInactive: {
    backgroundColor: 'white',
    color: '#374151',
    borderColor: '#d1d5db'
  },
  input: {
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  },
  select: {
    padding: '12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    backgroundColor: 'white',
    width: '100%',
    boxSizing: 'border-box'
  },
  addButton: {
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#3b82f6',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  deleteButton: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#ef4444',
    color: 'white',
    cursor: 'pointer',
    fontSize: '12px'
  }
};


  // Additional styles for budget manager
  const budgetStyles = {
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: '24px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',  // stack vertically on mobile
      alignItems: isMobile ? 'stretch' : 'flex-start'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    summaryCard: {
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      textAlign: 'center'
    },
    progressBar: {
      width: '100%',
      height: '12px',
      backgroundColor: '#e2e8f0',
      borderRadius: '6px',
      overflow: 'hidden',
      marginTop: '8px'
    },
    progressFill: {
      height: '100%',
      borderRadius: '6px',
      transition: 'width 0.3s ease'
    },
    expenseGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '12px',
      marginBottom: '16px'
    },
    categoryCard: {
      backgroundColor: '#f1f5f9',
      padding: '12px',
      borderRadius: '8px',
      textAlign: 'center',
      border: '1px solid #cbd5e1'
    },
    transactionItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px',
      backgroundColor: '#f9fafb',
      borderRadius: '6px',
      marginBottom: '8px',
      border: '1px solid #e5e7eb'
    }
  };

  const getCurrencySymbol = (currencyCode) => {
    const currency = availableCurrencies.find(c => c.code === currencyCode);
    return currency ? currency.symbol : currencyCode;
  };

  const expenseCategories = [
    'Food', 'Transport', 'Accommodation', 'Internet', 
    'Groceries', 'Utility', 'Laundry', 'Music', 
    'Trips', 'Tickets', 'Souvenirs', 'Others'
  ];

  const addMonthlyBudget = () => {
    const currentDate = new Date();
    const newMonth = {
      id: Date.now(),
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear(),
      target: budgetData.monthlyTarget,
      actual: 0
    };
    setMonthlyBudgets([...monthlyBudgets, newMonth]);
  };

  const updateMonthlyBudget = (id, field, value) => {
    setMonthlyBudgets(monthlyBudgets.map(month =>
      month.id === id ? { ...month, [field]: value } : month
    ));
  };

  const deleteMonthlyBudget = (id) => {
    setMonthlyBudgets(monthlyBudgets.filter(month => month.id !== id));
  };

  // New function to add monthly actual savings to current savings
  const addToCurrentSavings = (monthId) => {
    const month = monthlyBudgets.find(m => m.id === monthId);
    if (month && month.actual > 0) {
      setBudgetData(prev => ({
        ...prev,
        savingsGoal: {
          ...prev.savingsGoal,
          current: prev.savingsGoal.current + month.actual
        }
      }));
      // Reset the monthly actual to 0 after adding to savings
      updateMonthlyBudget(monthId, 'actual', 0);
    }
  };

  const addTransaction = (type) => {
    const newTransaction = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
    };

    if (type === 'expense') {
      setExpenses([newTransaction, ...expenses]);
    } else {
      setIncome([newTransaction, ...income]);
    }
  };

  const updateTransaction = (id, field, value, type) => {
    const updateArray = type === 'expense' ? expenses : income;
    const setArray = type === 'expense' ? setExpenses : setIncome;
    
    const updated = updateArray.map(transaction =>
      transaction.id === id ? { ...transaction, [field]: value } : transaction
    );
    setArray(updated);
  };

  const deleteTransaction = (id, type) => {
    if (type === 'expense') {
      setExpenses(expenses.filter(t => t.id !== id));
    } else {
      setIncome(income.filter(t => t.id !== id));
    }
  };

  const calculateTotals = () => {
    const totalIncome = income.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const totalExpenses = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const balance = totalIncome - totalExpenses;
    
    const categoryTotals = {};
    expenses.forEach(expense => {
      const category = expense.category || 'Others';
      categoryTotals[category] = (categoryTotals[category] || 0) + (parseFloat(expense.amount) || 0);
    });

    return { totalIncome, totalExpenses, balance, categoryTotals };
  };

  // Show loading if budgetData is not loaded yet
  if (!budgetData) {
    return (
      <div style={styles.todoContainer}>
        <div style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>
          Loading...
        </div>
      </div>
    );
  }

  const { totalIncome, totalExpenses, balance, categoryTotals } = calculateTotals();
  const savingsProgress = (budgetData.savingsGoal.current / budgetData.savingsGoal.target) * 100;

  const getBalanceColor = (amount) => {
    if (amount > 0) return '#10b981';
    if (amount < 0) return '#ef4444';
    return '#6b7280';
  };

  const formatAmount = (amount) => {
    return `${getCurrencySymbol(currency)}${Math.abs(amount).toFixed(2)}`;
  };

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1];
  };

  const renderOverview = () => (
    <>
      <div style={budgetStyles.summaryGrid}>
        <div style={budgetStyles.summaryCard}>
          <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>Savings Goal</h3>
          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#3b82f6'}}>
            {formatAmount(budgetData.savingsGoal.current)}
          </div>
          <div style={{fontSize: '14px', color: '#6b7280'}}>
            of {formatAmount(budgetData.savingsGoal.target)}
          </div>
          <div style={budgetStyles.progressBar}>
            <div 
              style={{
                ...budgetStyles.progressFill,
                width: `${Math.min(savingsProgress, 100)}%`,
                backgroundColor: '#3b82f6'
              }}
            />
          </div>
        </div>

        <div style={budgetStyles.summaryCard}>
          <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>This Month</h3>
          <div style={{fontSize: '24px', fontWeight: 'bold', color: getBalanceColor(balance)}}>
            {balance >= 0 ? '+' : '-'}{formatAmount(balance)}
          </div>
          <div style={{fontSize: '14px', color: '#6b7280'}}>
            Income: {formatAmount(totalIncome)}
          </div>
          <div style={{fontSize: '14px', color: '#6b7280'}}>
            Expenses: {formatAmount(totalExpenses)}
          </div>
        </div>

        <div style={budgetStyles.summaryCard}>
          <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>Monthly Target</h3>
          <div style={{fontSize: '24px', fontWeight: 'bold', color: '#10b981'}}>
            {formatAmount(budgetData.monthlyTarget)}
          </div>
          <div style={{fontSize: '14px', color: '#6b7280'}}>
            Time to goal: {budgetData.timeToGoal} months
          </div>
        </div>
      </div>

      <div style={{marginBottom: '24px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
          <h3 style={{margin: 0, color: '#374151'}}>📅 Monthly Progress</h3>
          <button onClick={addMonthlyBudget} style={{...styles.addButton, padding: '8px 16px'}}>
            ➕ Add Month
          </button>
        </div>
        
        {monthlyBudgets.length === 0 ? (
          <p style={{color: '#6b7280', fontStyle: 'italic', textAlign: 'center', padding: '20px'}}>
            No monthly budgets added yet. Click "Add Month" to start tracking!
          </p>
        ) : (
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px'}}>
            {monthlyBudgets.map(month => (
              <div key={month.id} style={{...budgetStyles.categoryCard, textAlign: 'left'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <select
                      value={month.month}
                      onChange={(e) => updateMonthlyBudget(month.id, 'month', parseInt(e.target.value))}
                      style={{...styles.select, padding: '4px', width: '70px', fontSize: '12px'}}
                    >
                      {Array.from({length: 12}, (_, i) => (
                        <option key={i + 1} value={i + 1}>{getMonthName(i + 1)}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={month.year}
                      onChange={(e) => updateMonthlyBudget(month.id, 'year', parseInt(e.target.value) || new Date().getFullYear())}
                      style={{...styles.input, padding: '4px', width: '70px', fontSize: '12px'}}
                    />
                  </div>
                  <button
                    onClick={() => deleteMonthlyBudget(month.id)}
                    style={{...styles.deleteButton, padding: '4px', fontSize: '12px'}}
                  >
                    🗑️
                  </button>
                </div>
                <div style={{marginBottom: '8px'}}>
                  <label style={{fontSize: '12px', color: '#6b7280'}}>Target:</label>
                  <input
                    type="number"
                    value={month.target}
                    onChange={(e) => updateMonthlyBudget(month.id, 'target', parseFloat(e.target.value) || 0)}
                    style={{...styles.input, padding: '4px', marginLeft: '8px', width: '80px'}}
                  />
                </div>
                <div style={{marginBottom: '12px'}}>
                  <label style={{fontSize: '12px', color: '#6b7280'}}>Actual:</label>
                  <input
                    type="number"
                    value={month.actual}
                    onChange={(e) => updateMonthlyBudget(month.id, 'actual', parseFloat(e.target.value) || 0)}
                    style={{...styles.input, padding: '4px', marginLeft: '8px', width: '80px'}}
                  />
                </div>
                <div style={{marginBottom: '12px', fontSize: '14px', fontWeight: 'bold'}}>
                  Progress: <span style={{color: month.actual >= month.target ? '#10b981' : '#ef4444'}}>
                    {month.target > 0 ? ((month.actual / month.target) * 100).toFixed(1) : 0}%
                  </span>
                </div>
                {month.actual > 0 && (
                  <button
                    onClick={() => addToCurrentSavings(month.id)}
                    style={{
                      ...styles.addButton,
                      backgroundColor: '#10b981',
                      padding: '6px 12px',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    💰 Add {formatAmount(month.actual)} to Savings
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{marginBottom: '24px'}}>
        <h3 style={{marginBottom: '16px', color: '#374151'}}>Expense Categories</h3>
        <div style={budgetStyles.expenseGrid}>
          {expenseCategories.map(category => (
            <div key={category} style={budgetStyles.categoryCard}>
              <div style={{fontSize: '14px', fontWeight: '600', marginBottom: '4px'}}>
                {category}
              </div>
              <div style={{fontSize: '18px', fontWeight: 'bold', color: '#ef4444'}}>
                {formatAmount(categoryTotals[category] || 0)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const renderTransactions = () => (
    <>
      <div style={{display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap'}}>
        <button 
          onClick={() => addTransaction('income')} 
          style={{...styles.addButton, backgroundColor: '#10b981'}}
        >
          ➕ Add Income
        </button>
        <button 
          onClick={() => addTransaction('expense')} 
          style={{...styles.addButton, backgroundColor: '#ef4444'}}
        >
          ➖ Add Expense
        </button>
      </div>

      <div style={{marginBottom: '32px'}}>
        <h3 style={{color: '#10b981', marginBottom: '16px'}}>💰 Income</h3>
        {income.length === 0 ? (
          <p style={{color: '#6b7280', fontStyle: 'italic'}}>No income recorded yet</p>
        ) : (
          income.map(item => (
            <div key={item.id} style={budgetStyles.transactionItem}>
              <div style={{flex: 1, display: 'grid', gridTemplateColumns: '120px 2fr 1fr 120px', gap: '12px', alignItems: 'center'}}>
                <input
                  type="date"
                  value={item.date}
                  onChange={(e) => updateTransaction(item.id, 'date', e.target.value, 'income')}
                  style={{...styles.input, padding: '8px'}}
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateTransaction(item.id, 'description', e.target.value, 'income')}
                  placeholder="Description"
                  style={{...styles.input, padding: '8px'}}
                />
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => updateTransaction(item.id, 'category', e.target.value, 'income')}
                  placeholder="Source"
                  style={{...styles.input, padding: '8px'}}
                />
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateTransaction(item.id, 'amount', e.target.value, 'income')}
                    placeholder="0.00"
                    style={{...styles.input, padding: '8px', width: '80px'}}
                    step="0.01"
                  />
                  <button
                    onClick={() => deleteTransaction(item.id, 'income')}
                    style={{...styles.deleteButton, padding: '6px'}}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div>
        <h3 style={{color: '#ef4444', marginBottom: '16px'}}>💸 Expenses</h3>
        {expenses.length === 0 ? (
          <p style={{color: '#6b7280', fontStyle: 'italic'}}>No expenses recorded yet</p>
        ) : (
          expenses.map(item => (
            <div key={item.id} style={budgetStyles.transactionItem}>
              <div style={{flex: 1, display: 'grid', gridTemplateColumns: '120px 2fr 120px 120px', gap: '12px', alignItems: 'center'}}>
                <input
                  type="date"
                  value={item.date}
                  onChange={(e) => updateTransaction(item.id, 'date', e.target.value, 'expense')}
                  style={{...styles.input, padding: '8px'}}
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateTransaction(item.id, 'description', e.target.value, 'expense')}
                  placeholder="What did you buy?"
                  style={{...styles.input, padding: '8px'}}
                />
                <select
                  value={item.category}
                  onChange={(e) => updateTransaction(item.id, 'category', e.target.value, 'expense')}
                  style={{...styles.select, padding: '8px'}}
                >
                  {expenseCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateTransaction(item.id, 'amount', e.target.value, 'expense')}
                    placeholder="0.00"
                    style={{...styles.input, padding: '8px', width: '80px'}}
                    step="0.01"
                  />
                  <button
                    onClick={() => deleteTransaction(item.id, 'expense')}
                    style={{...styles.deleteButton, padding: '6px'}}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div>
        <h3 style={{color: '#ef4444', marginBottom: '16px'}}>💸 Expenses</h3>
        {expenses.length === 0 ? (
          <p style={{color: '#6b7280', fontStyle: 'italic'}}>No expenses recorded yet</p>
        ) : (
          expenses.map(item => (
            <div key={item.id} style={budgetStyles.transactionItem}>
              <div style={{flex: 1, display: 'grid', gridTemplateColumns: '120px 2fr 120px 120px', gap: '12px', alignItems: 'center'}}>
                <input
                  type="date"
                  value={item.date}
                  onChange={(e) => updateTransaction(item.id, 'date', e.target.value, 'expense')}
                  style={{...styles.input, padding: '8px'}}
                />
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateTransaction(item.id, 'description', e.target.value, 'expense')}
                  placeholder="What did you buy?"
                  style={{...styles.input, padding: '8px'}}
                />
                <select
                  value={item.category}
                  onChange={(e) => updateTransaction(item.id, 'category', e.target.value, 'expense')}
                  style={{...styles.select, padding: '8px'}}
                >
                  {expenseCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateTransaction(item.id, 'amount', e.target.value, 'expense')}
                    placeholder="0.00"
                    style={{...styles.input, padding: '8px', width: '80px'}}
                    step="0.01"
                  />
                  <button
                    onClick={() => deleteTransaction(item.id, 'expense')}
                    style={{...styles.deleteButton, padding: '6px'}}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  const renderSavingsGoals = () => (
    <>
      <div style={styles.addTaskSection}>
        <h3 style={{marginBottom: '16px', color: '#374151'}}>💎 Savings Goal Settings</h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
          <div>
            <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
              Total Savings Goal
            </label>
            <input
              type="number"
              value={budgetData.savingsGoal.target}
              onChange={(e) => setBudgetData(prev => ({
                ...prev,
                savingsGoal: { ...prev.savingsGoal, target: parseFloat(e.target.value) || 0 }
              }))}
              style={styles.input}
              step="100"
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
              Current Savings
            </label>
            <input
              type="number"
              value={budgetData.savingsGoal.current}
              onChange={(e) => setBudgetData(prev => ({
                ...prev,
                savingsGoal: { ...prev.savingsGoal, current: parseFloat(e.target.value) || 0 }
              }))}
              style={styles.input}
              step="0.01"
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
              Monthly Target
            </label>
            <input
              type="number"
              value={budgetData.monthlyTarget}
              onChange={(e) => setBudgetData(prev => ({
                ...prev,
                monthlyTarget: parseFloat(e.target.value) || 0
              }))}
              style={styles.input}
              step="10"
            />
          </div>
          <div>
            <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151'}}>
              Months to Goal
            </label>
            <input
              type="number"
              value={budgetData.timeToGoal}
              onChange={(e) => setBudgetData(prev => ({
                ...prev,
                timeToGoal: parseFloat(e.target.value) || 1
              }))}
              style={styles.input}
              step="1"
              min="1"
            />
          </div>
        </div>
      </div>

      <div style={styles.addTaskSection}>
        <h3 style={{marginBottom: '16px', color: '#374151'}}>💰 Progress Visualization</h3>
        <div style={{marginBottom: '16px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
            <span style={{fontWeight: '600'}}>Savings Progress</span>
            <span>{savingsProgress.toFixed(1)}%</span>
          </div>
          <div style={budgetStyles.progressBar}>
            <div 
              style={{
                ...budgetStyles.progressFill,
                width: `${Math.min(savingsProgress, 100)}%`,
                backgroundColor: savingsProgress >= 100 ? '#10b981' : '#3b82f6'
              }}
            />
          </div>
        </div>
        
        <div style={budgetStyles.summaryGrid}>
          <div style={budgetStyles.summaryCard}>
            <div style={{fontSize: '18px', fontWeight: 'bold', color: '#3b82f6'}}>
              {formatAmount(budgetData.savingsGoal.target - budgetData.savingsGoal.current)}
            </div>
            <div style={{fontSize: '14px', color: '#6b7280'}}>Remaining to goal</div>
          </div>
          <div style={budgetStyles.summaryCard}>
            <div style={{fontSize: '18px', fontWeight: 'bold', color: '#10b981'}}>
              {budgetData.monthlyTarget > 0 ? 
                Math.ceil((budgetData.savingsGoal.target - budgetData.savingsGoal.current) / budgetData.monthlyTarget) : 
                0
              }
            </div>
            <div style={{fontSize: '14px', color: '#6b7280'}}>Months to goal (at current target)</div>
          </div>
          <div style={budgetStyles.summaryCard}>
            <div style={{fontSize: '18px', fontWeight: 'bold', color: '#8b5cf6'}}>
              {budgetData.timeToGoal > 0 ? 
                formatAmount((budgetData.savingsGoal.target - budgetData.savingsGoal.current) / budgetData.timeToGoal) : 
                formatAmount(0)
              }
            </div>
            <div style={{fontSize: '14px', color: '#6b7280'}}>Required per month (based on your timeline)</div>
          </div>
        </div>
      </div>
    </>
  );

  if (!budgetData) {
        return <div>Loading...</div>;
    }

  return (
     <div style={styles.todoContainer}>
         <div style={styles.todoHeader}>
            <h2 style={styles.todoTitle}>💰 Budget Manager</h2>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span style={{fontSize: '12px', color: '#10b981'}}>✓ Auto-saved</span>
              <button onClick={clearAllData} style={clearDataButtonStyle}>
                🗑️ Clear All Data
              </button>
            </div>
  </div>

      <div style={budgetStyles.tabContainer}>
        {[
          { id: 'overview', label: '📊 Overview' },
          { id: 'transactions', label: '💳 Transactions' },
          { id: 'goals', label: '🎯 Savings Goals' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.filterButton,
              ...(activeTab === tab.id ? styles.filterActive : styles.filterInactive)
            }}
          >
            {tab.label}
          </button>
        ))}
        <select
  value={currency}
  onChange={(e) => setCurrency(e.target.value)}
  style={{
    ...styles.select,
    width: 'auto',
    marginLeft: 'auto'   // pushes it to the far right
  }}
>
  {availableCurrencies.map(curr => (
    <option key={curr.code} value={curr.code}>
      {curr.symbol} {curr.name}
    </option>
  ))}
</select>
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'transactions' && renderTransactions()}
      {activeTab === 'goals' && renderSavingsGoals()}
    </div>
  );
};

export default BudgetManager;